import {
   memo,
   FC,
   Fragment,
   useCallback,
   useEffect,
   useMemo,
   useState,
} from "react"
import { useDispatch, useSelector } from "react-redux"
import AlarmSoundPrompt from "@/components/AlarmSoundPrompt"
import AlertCard from "@/components/AlertCard"
import { SoundFrequency } from "@/components/AlertSoundSettings/files"
import AlertWearableCard from "@/components/AlertWearableCard"
import { AudioPlayer } from "@/components/AudioPlayer"
import Loader from "@/components/Loader"
import Button, { ButtonIconPosition } from "@/components/shared/Button"
import Dialog from "@/components/shared/Dialog"
import LazyMount from "@/components/shared/LazyMount"
import Link from "@/components/shared/Link"
import { SpinnerSize } from "@/components/shared/Spinner"
import Tooltip, { TooltipPosition } from "@/components/shared/Tooltip"
import { HttpCodes } from "@/constants/globals"
import { PageRoutes } from "@/constants/routes"
import { useFastClearAlert } from "@/hooks/useFastClearAlert"
import { AppDispatch, RootState } from "@/store"
import { clearAlerts } from "@/store/alertsSlice"
import {
   AudioPlaybackStatus,
   resetAudioPlayback,
   setPlaybackStatus,
   setPlayingAlertId,
   setPlayingAlertPriority,
} from "@/store/audioPlaybackSlice"
import {
   Alert,
   AlertSource,
   AlertVariant,
   TypefaceVariant,
} from "@/types/alert"
import { ButtonVariant } from "@/types/index"
import { getAlertSound } from "@/utils/alerts"
import { formatInmateName, getErrorMessage } from "@/utils/index"

interface AlertBarProps {
   loadingAlerts: boolean
   alertsError: number | null
   alertsData: Alert[] | undefined
   loadingClearAlert: boolean
   loadingClearAlerts: boolean
   alertIdToClear: string | null
   facilityId: string | null
   isSuperAdmin: boolean | null
   handleAlertClick: (alert: Alert) => void
   handleSwapDeviceClick: (inmateId: string) => void
}
interface DialogState {
   isOpen: boolean
   title: string
   buttonTitle: string
   onConfirm: (() => void) | null
}

const AlertBar: FC<AlertBarProps> = ({
   loadingAlerts,
   alertsError,
   alertsData,
   loadingClearAlert,
   loadingClearAlerts,
   alertIdToClear,
   facilityId,
   isSuperAdmin,
   handleAlertClick,
   handleSwapDeviceClick,
}) => {
   const dispatch = useDispatch<AppDispatch>()
   const { firstName, lastName } = useSelector(
      ({ userReducer }: RootState) => userReducer
   )

   const sortedAlertsData = useMemo(
      () =>
         alertsData
            ? [...alertsData].sort((a, b) => a.priority - b.priority)
            : undefined,
      [alertsData]
   )

   const [dialogState, setDialogState] = useState<DialogState>({
      isOpen: false,
      title: "",
      buttonTitle: "",
      onConfirm: null,
   })
   const { handleClearAlert } = useFastClearAlert()
   const { playingAlertId, playingAlertPriority, status } = useSelector(
      ({ audioPlaybackReducer }: RootState) => audioPlaybackReducer
   )

   const setAudioPlayback = useCallback(
      (alertId: string, alertPriority: number) => {
         dispatch(setPlayingAlertId(alertId))
         dispatch(setPlayingAlertPriority(alertPriority))
         dispatch(setPlaybackStatus(AudioPlaybackStatus.PLAYING))
      },
      [dispatch]
   )

   useEffect(() => {
      if (playingAlertId) {
         const playingAlertWasCleared = !alertsData?.some(
            (alert) => alert.id === playingAlertId
         )
         if (playingAlertWasCleared) {
            dispatch(resetAudioPlayback())
         }
      }

      if (!alertsData || alertsData.length === 0) return

      const playingAlert = alertsData.find((a) => a.soundPlaying)

      if (!playingAlert) return

      const shouldSetAudioPlayback =
         status === AudioPlaybackStatus.FINISHED ||
         (playingAlertPriority !== null &&
            playingAlert.priority < playingAlertPriority)

      if (shouldSetAudioPlayback) {
         setAudioPlayback(playingAlert.id, playingAlert.priority)
      }
   }, [
      alertsData,
      dispatch,
      playingAlertPriority,
      setAudioPlayback,
      status,
      playingAlertId,
   ])

   useEffect(() => {
      return () => {
         dispatch(resetAudioPlayback())
      }
   }, [dispatch])

   const handleClearAlertClick = (alert: Alert) => {
      setDialogState({
         isOpen: true,
         title: "Please confirm to clear this alarm",
         buttonTitle: "Clear Alarm",
         onConfirm: () =>
            handleClearAlert(alert.id, () =>
               setDialogState({
                  isOpen: false,
                  title: "",
                  buttonTitle: "",
                  onConfirm: null,
               })
            ),
      })
   }

   const handleClearAlerts = useCallback(async () => {
      try {
         const result = await dispatch(
            clearAlerts({
               clearReport: {
                  name: `${firstName} ${lastName}`,
                  reportNote: "Fast clearance",
               },
            })
         ).unwrap()
         if (result.status === HttpCodes.OK) {
            setDialogState({
               isOpen: false,
               title: "",
               buttonTitle: "",
               onConfirm: null,
            })
         }
      } catch (error) {
         console.error("Error fast clearing alerts:", error)
      }
   }, [dispatch, firstName, lastName])

   const openClearAllDialog = () => {
      setDialogState({
         isOpen: true,
         title: "Please confirm fast clearance",
         buttonTitle: "Clear all alarms",
         onConfirm: handleClearAlerts,
      })
   }

   const handleDialogConfirm = () => {
      if (dialogState.onConfirm) {
         dialogState.onConfirm()
      }
   }

   const handleDialogClose = () => {
      setDialogState({
         isOpen: false,
         title: "",
         buttonTitle: "",
         onConfirm: null,
      })
   }

   return (
      <>
         <AlarmSoundPrompt />
         <span className="header__alert">
            {facilityId === null && (
               <span className="header__alert-select-facility">
                  {isSuperAdmin && (
                     <>
                        Please select a facility from&nbsp;
                        <Link to={PageRoutes.CONTROL_PANEL}>
                           Tenant Admin Control Panel
                        </Link>
                        &nbsp;to view alerts
                     </>
                  )}
               </span>
            )}
            {!loadingAlerts && alertsError ? (
               <span className="header__alert--error">
                  {getErrorMessage(alertsError)}
               </span>
            ) : (
               <>
                  {alertsData && alertsData?.length > 1 && (
                     <span className="header__alert-controls">
                        {/* <Icon
               name="clock"
               color={IconColor.COOL_GRAY}
               size="30"
            />
            <Icon
               name="alerts"
               color={IconColor.COOL_GRAY}
               size="30"
            /> */}
                        {isSuperAdmin && (
                           <Tooltip
                              content="Clear Alarms"
                              position={TooltipPosition.RIGHT}
                           >
                              <Button
                                 iconName="clearAlerts"
                                 iconPosition={ButtonIconPosition.LEFT}
                                 variant={ButtonVariant.SMALL}
                                 onClick={openClearAllDialog}
                              />
                              {/* <Icon
                                 name="clearAlerts"
                                 color={IconColor.HONOLULU_BLUE}
                                 size="30"
                                 onClick={openModal}
                              /> */}
                           </Tooltip>
                        )}
                     </span>
                  )}
                  {sortedAlertsData?.map((alert) => {
                     if (loadingClearAlert && alert.id === alertIdToClear) {
                        return (
                           <span
                              key={`alert-clearing-${alert.id}`}
                              className="header__alert--clearing-loading"
                           >
                              <Loader
                                 fullHeight={false}
                                 size={SpinnerSize.SMALL}
                              />
                           </span>
                        )
                     }

                     const alertSound = getAlertSound(
                        alert.alertConfiguration.sound
                     )
                     const playingAlert = alert.id === playingAlertId

                     return (
                        <Fragment key={`alert-fragment-${alert.id}`}>
                           {playingAlert &&
                              status === AudioPlaybackStatus.PLAYING && (
                                 <AudioPlayer
                                    key={`alert-sound-${alert.id}`}
                                    autoPlay
                                    stopOthers={false}
                                    src={alertSound.value}
                                    type={alertSound.type}
                                    loop={
                                       alert.alertConfiguration.sound
                                          .frequency ===
                                       SoundFrequency.CONTINUOUS
                                    }
                                 />
                              )}
                           <LazyMount
                              key={`alert-lazy-mount-${alert.id}`}
                              className="header__alert-card-placeholder"
                           >
                              {alert.source === AlertSource.WEARABLE ? (
                                 <AlertWearableCard
                                    inmateName={
                                       alert.inmate?.customIdentifier ||
                                       formatInmateName(alert.inmate ?? null)
                                    }
                                    locationName={alert.level.name ?? ""}
                                    vitals={{
                                       ...alert.vitals,
                                       lowOxygenLevel:
                                          alert.vitals.lowOxygenLevel,
                                    }}
                                    systemConditionType={
                                       alert.alertConfiguration.systemCondition
                                          ?.type
                                    }
                                    priority={alert.priority}
                                    alertVariant={
                                       alert.alertConfiguration.color.card.toLowerCase() as keyof typeof AlertVariant
                                    }
                                    typefaceColor={
                                       alert.alertConfiguration.color.typeface.toLowerCase() as keyof typeof TypefaceVariant
                                    }
                                    dateTime={alert.timestamp}
                                    onClick={() => handleAlertClick(alert)}
                                    onClearAlert={() =>
                                       handleClearAlertClick(alert)
                                    }
                                    onSwapDevice={() => {
                                       if (alert.inmate?.id) {
                                          handleSwapDeviceClick(alert.inmate.id)
                                       }
                                    }}
                                 />
                              ) : (
                                 <AlertCard
                                    cellId={alert.level.name}
                                    // bpm={alert.heartRate}
                                    // rpm={alert.respiratoryRate}
                                    isCustom={alert.alertConfiguration.custom}
                                    hasDoorSensor={alert.level.hasDoorSensor}
                                    systemConditionType={
                                       alert.alertConfiguration.systemCondition
                                          ?.type
                                    }
                                    priority={alert.priority}
                                    alertVariant={
                                       alert.alertConfiguration.color.card.toLowerCase() as keyof typeof AlertVariant
                                    }
                                    typefaceColor={
                                       alert.alertConfiguration.color.typeface.toLowerCase() as keyof typeof TypefaceVariant
                                    }
                                    dateTime={alert.timestamp}
                                    vitals={alert.vitals}
                                    onClick={() => handleAlertClick(alert)}
                                    onClearAlert={() =>
                                       handleClearAlertClick(alert)
                                    }
                                 />
                              )}
                           </LazyMount>
                        </Fragment>
                     )
                  })}
               </>
            )}
         </span>
         {dialogState.isOpen && (
            <Dialog
               open={dialogState.isOpen}
               title={dialogState.title}
               buttonTitle={dialogState.buttonTitle}
               isDisabled={loadingClearAlert || loadingClearAlerts}
               handleClose={handleDialogClose}
               handleButtonClick={handleDialogConfirm}
            />
         )}
      </>
   )
}

export default memo(AlertBar)
